import { createContext, useContext } from "react";
import type { VerifiedDisplayPrice } from "@/lib/presentation-text";
import type { ReactNode } from "react";
import { useCurrency } from "@/lib/currency";
import { useActiveLang } from "@/hooks/use-active-lang";
import { useTourCardPrice } from "@/lib/tour-card-batch";

export const PresentationPriceContext = createContext<Record<string, VerifiedDisplayPrice | null>>({});

const NO_PRICE = { es: "Ver precio y fechas", en: "See price and dates", fr: "Voir prix et dates", it: "Vedi prezzo e date", de: "Preis und Termine ansehen", pt: "Ver preço e datas" } as const;
const VEHICLE = { es: "/ vehículo", en: "/ vehicle", fr: "/ véhicule", it: "/ veicolo", de: "/ Fahrzeug", pt: "/ veículo" } as const;
const ADULT = { es: "por adulto", en: "per adult", fr: "par adulte", it: "per adulto", de: "pro Erwachsenem", pt: "por adulto" } as const;

/** Cached one-adult final price (taxes/fees included); live quote only in the calendar.
 *  Loading → grey skeleton; no price → "Ver precio y fechas"; never a dash. */
export function FinalTourPrice({ id, unitLabel, context }: { id: string; unitLabel?: string; context?: ReactNode }) {
  const { format, currency } = useCurrency();
  const lang = useActiveLang();
  const initial = useContext(PresentationPriceContext)[id.replace(/^bkn-/, "")];
  const { data: p, isPending } = useTourCardPrice(id, initial);
  if (isPending) return <span aria-hidden className="inline-block h-[1em] w-24 animate-pulse rounded bg-muted align-middle" />;
  if (!p) return <span className="text-[12.5px] font-semibold leading-tight text-primary">{NO_PRICE[lang as keyof typeof NO_PRICE] ?? NO_PRICE.es}</span>;
  return (
    <>
      <span>
        {format(p.amount, p.currency, { locale: lang, decimals: 2 })} {currency}
        {unitLabel && <span className="ml-1 text-xs font-normal text-muted-foreground">{p.unit === "vehicle" ? VEHICLE[lang] : p.unit === "adult" ? ADULT[lang] : unitLabel}</span>}
      </span>
      {context}
    </>
  );
}
